import React, { useState, useEffect, useCallback } from 'react'
import { getBackendUrl } from '../env'
import { System } from '../context/SystemContext'
import * as programHelper from '../helper/programHelper'
import { useAuthContext } from '../hooks/useAuthContext'
import { Program } from '../types'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import dayjs from 'dayjs'
import { ChevronDown, ChevronUp, Pencil } from 'lucide-react'
import RangeSlider from './Sliders/RangeSlider'
import TimeSlider from './Sliders/TimeSlider'
import MultiSelectCheckbox from './MultiSelectCheckbox'
import { editingSensorGroups } from './SystemForm'
import MultiSelectCheckboxSensors from './MultiSelectCheckboxSensors'
import DoserSettings from './SystemSettings/DoserSettings'
import {
    DEFAULT_AUTOMATION_SAFETY,
    mergeAutomationSafetyIntoAdvancedOptions,
} from '@/lib/automationSafetyDefaults'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const DEFAULT_ADVANCED_OPTIONS = {
    lights: {
        light_start_time: '06:00',
        light_end_time: '18:00',
    },
    dose_amount: 10,
    time_between_doses: {
        hour: 0,
        minute: 0,
        seconds: 30,
    },
    time_before_readjustment: {
        hour: 0,
        minute: 10,
        seconds: 0,
    },
    tolerances: {
        ph: [0.25, 0.25] as [number, number],
        ec: [0.01, 0.01] as [number, number],
        temperature: [2, 2] as [number, number],
        humidity: [5, 5] as [number, number],
    },
    mixing_time: {
        minute: 0,
        seconds: 30,
    },
    pump_all_day: true,
    pump_start_time: '06:00',
    pump_end_time: '18:00',
    pump_duration: {
        minute: 0,
        seconds: 30,
    },
    pump_interval: {
        hour: 0,
        minute: 10,
    },
    automation_safety: {
        toggle: { ...DEFAULT_AUTOMATION_SAFETY.toggle },
        dose: { ...DEFAULT_AUTOMATION_SAFETY.dose },
        hysteresis: { ...DEFAULT_AUTOMATION_SAFETY.hysteresis },
    },
}

interface AutomaticProgramProps {
    setProgram: React.Dispatch<React.SetStateAction<Program>>
    programType: string
    system: System
    changingProgram: boolean
    setChangingProgram: React.Dispatch<React.SetStateAction<boolean>>
    program: Program
    defaultOptions: boolean
    advancedOptions: object
    setDefaultOptions: React.Dispatch<React.SetStateAction<boolean>>
    setAdvancedOptions: React.Dispatch<React.SetStateAction<object>>
    sensorGroup: any[]
    setSensorGroup: React.Dispatch<React.SetStateAction<any[]>>
    editingSensorGroups: editingSensorGroups
    setEditingSensorGroups: React.Dispatch<React.SetStateAction<editingSensorGroups>>
}

function convertDateFormat(inputDate: string) {
    const date = new Date(inputDate)
    const year = date.getFullYear()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)
    const hours = ('0' + date.getHours()).slice(-2)
    const minutes = ('0' + date.getMinutes()).slice(-2)
    return `${year}-${month}-${day}T${hours}:${minutes}`
}

const AutomaticProgram = ({
    program,
    programType,
    setProgram,
    changingProgram,
    setChangingProgram,
    system,
    defaultOptions,
    advancedOptions: _advancedOptionsFromParent,
    setAdvancedOptions,
    setDefaultOptions,
    sensorGroup,
    setSensorGroup,
    editingSensorGroups,
    setEditingSensorGroups,
}: AutomaticProgramProps) => {
    const [programList, setProgramList] = useState<Program[]>([])
    const [completedSystems, setCompletedSystems] = useState<any[]>([])
    const [advancedOptionsState, setAdvancedOptionsState] = useState<Record<string, any>>(DEFAULT_ADVANCED_OPTIONS)
    const { user } = useAuthContext()

    const [programOpen, setProgramOpen] = useState(true)
    const [groupsOpen, setGroupsOpen] = useState(true)
    const [advancedOpen, setAdvancedOpen] = useState(false)

    const getPrograms = useCallback(async () => {
        const response = await fetch(`${getBackendUrl()}/api/programs/`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
        const json = await response.json()
        if (response.ok) setProgramList(Array.isArray(json) ? json : [])
    }, [])

    const getCompleteSystems = useCallback(async () => {
        if (user !== null) {
            const systems = await programHelper.checkForCompleteSystems(system, user)
            setCompletedSystems(Array.isArray(systems) ? systems : [])
        }
    }, [user, system])

    useEffect(() => {
        void getPrograms()
    }, [getPrograms])

    useEffect(() => {
        void getCompleteSystems()
    }, [getCompleteSystems, programType])

    useEffect(() => {
        if (system instanceof Object && '_id' in system && system.program !== undefined) {
            setProgram(system.program)
        }
        if (system instanceof Object && '_id' in system && system.advanced_options !== undefined) {
            setAdvancedOptionsState(
                mergeAutomationSafetyIntoAdvancedOptions(
                    system.advanced_options as Record<string, unknown>
                ) as Record<string, any>
            )
        }
    }, [system, setProgram])

    useEffect(() => {
        if (defaultOptions === true) {
            setAdvancedOptions(DEFAULT_ADVANCED_OPTIONS)
        } else {
            setAdvancedOptions(advancedOptionsState)
        }
    }, [advancedOptionsState, defaultOptions, setAdvancedOptions])

    const handleEdit = async (e: string, type: string) => {
        const combined = e + '-' + type
        switch (combined) {
            case 'program-':
                setChangingProgram(!changingProgram)
                break
            case 'pH-edit_sensors':
                setEditingSensorGroups((prev) => ({ ...prev, ph_sensor: !prev.ph_sensor }))
                break
            case 'EC-edit_sensors':
                setEditingSensorGroups((prev) => ({ ...prev, ec_sensor: !prev.ec_sensor }))
                break
            case 'Temperature-edit_sensors':
                setEditingSensorGroups((prev) => ({ ...prev, temperature_sensor: !prev.temperature_sensor }))
                break
            case 'Humidity-edit_sensors':
                setEditingSensorGroups((prev) => ({ ...prev, humidity_sensor: !prev.humidity_sensor }))
                break
            case 'Moisture-edit_sensors':
                setEditingSensorGroups((prev) => ({ ...prev, moisture_sensor: !prev.moisture_sensor }))
                break
            case 'pH-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, ph_device: !prev.ph_device }))
                break
            case 'EC-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, ec_device: !prev.ec_device }))
                break
            case 'Temperature-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, temperature_device: !prev.temperature_device }))
                break
            case 'Humidity-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, humidity_device: !prev.humidity_device }))
                break
            case 'Moisture-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, moisture_device: !prev.moisture_device }))
                break
            case 'Water Circulation-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, pump_device: !prev.pump_device }))
                break
            case 'Lights-edit_controllers':
                setEditingSensorGroups((prev) => ({ ...prev, light_device: !prev.light_device }))
                break
        }
    }

    const chooseGroupType = (variable: string, type: string, controllersInfo: any) => {
        const combined = variable + '-' + type
        const sensorGroupRoleName: any[] = []
        sensorGroup.forEach((sys) => {
            if (sys.variable === variable && sys.controllers_info) {
                sys.controllers_info.forEach((controller: any) => {
                    sensorGroupRoleName.push(controller.role)
                })
            }
        })
        const sensors: any[] = []
        sensorGroup.forEach((sys) => {
            if (sys.variable === variable && sys.sensors !== undefined) {
                sys.sensors.forEach((sensor: any) => {
                    sensors.push(sensor.name)
                })
            }
        })
        switch (combined) {
            case 'pH-controllers':
                if (editingSensorGroups.ph_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'EC-controllers':
                if (editingSensorGroups.ec_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'Temperature-controllers':
                if (editingSensorGroups.temperature_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'Humidity-controllers':
                if (editingSensorGroups.humidity_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'Moisture-controllers':
                if (editingSensorGroups.moisture_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'pH-sensors':
                if (editingSensorGroups.ph_sensor)
                    return (
                        <MultiSelectCheckboxSensors
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensors.join(', ')
            case 'EC-sensors':
                if (editingSensorGroups.ec_sensor)
                    return (
                        <MultiSelectCheckboxSensors
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensors.join(', ')
            case 'Temperature-sensors':
                if (editingSensorGroups.temperature_sensor)
                    return (
                        <MultiSelectCheckboxSensors
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensors.join(', ')
            case 'Humidity-sensors':
                if (editingSensorGroups.humidity_sensor)
                    return (
                        <MultiSelectCheckboxSensors
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensors.join(', ')
            case 'Moisture-sensors':
                if (editingSensorGroups.moisture_sensor)
                    return (
                        <MultiSelectCheckboxSensors
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensors.join(', ')
            case 'Water Circulation-controllers':
                if (editingSensorGroups.pump_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            case 'Lights-controllers':
                if (editingSensorGroups.light_device)
                    return (
                        <MultiSelectCheckbox
                            completedSystems={completedSystems}
                            variable={variable}
                            sensorGroup={sensorGroup}
                            setSensorGroup={setSensorGroup}
                            controllersInfo={controllersInfo}
                        />
                    )
                return sensorGroupRoleName.join(', ')
            default:
                return '—'
        }
    }

    const sectionHeaderBtn = (open: boolean, setOpen: (v: boolean) => void) => (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
        >
            {open ? (
                <>
                    <ChevronUp className="size-4" aria-hidden />
                    Hide
                </>
            ) : (
                <>
                    <ChevronDown className="size-4" aria-hidden />
                    Show
                </>
            )}
        </Button>
    )

    const fieldLabel = 'text-xs font-semibold uppercase tracking-wide text-zinc-500'

    return (
        <div className="space-y-4 text-zinc-100">
            <Card className="overflow-visible border-zinc-700 bg-zinc-950/75 shadow-lg ring-1 ring-zinc-800/60">
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                    <div className="min-w-0">
                        <CardTitle className="text-base text-zinc-50">Grow program</CardTitle>
                        <CardDescription className="text-zinc-400">
                            Select a recipe and review targets for automatic control.
                        </CardDescription>
                    </div>
                    {sectionHeaderBtn(programOpen, setProgramOpen)}
                </CardHeader>
                {programOpen ? (
                    <CardContent className="space-y-4 overflow-visible pt-0">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <p className="text-sm font-medium text-zinc-200">{program.name || '—'}</p>
                                <p className="text-xs text-zinc-500">Current program</p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-zinc-600 bg-zinc-900 hover:bg-zinc-800"
                                onClick={() => void handleEdit('program', '')}
                            >
                                <Pencil className="mr-1.5 size-3.5" aria-hidden />
                                {changingProgram ? 'Done' : 'Change program'}
                            </Button>
                        </div>

                        {changingProgram ? (
                            <div className="space-y-3">
                                <label className={fieldLabel}>Choose program</label>
                                <select
                                    className="w-full max-w-md rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
                                    defaultValue="DEFAULT"
                                    onChange={(e) => setProgram(JSON.parse(e.target.value))}
                                >
                                    <option value="DEFAULT" disabled>
                                        Select program…
                                    </option>
                                    {programList.map((p) => (
                                        <option key={p._id} value={JSON.stringify(p)}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : null}

                        <div className="overflow-x-auto rounded-lg border border-zinc-800">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-zinc-800 bg-zinc-900/80 text-left text-xs uppercase tracking-wide text-zinc-400">
                                        <th className="px-3 py-2 font-medium">Temp (°F)</th>
                                        <th className="px-3 py-2 font-medium">Humidity (%)</th>
                                        <th className="px-3 py-2 font-medium">EC</th>
                                        <th className="px-3 py-2 font-medium">pH</th>
                                        <th className="px-3 py-2 font-medium">Lights (hrs)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="px-3 py-2 text-zinc-200">{program.temperature}</td>
                                        <td className="px-3 py-2 text-zinc-200">{program.humidity}</td>
                                        <td className="px-3 py-2 text-zinc-200">{program.ec}</td>
                                        <td className="px-3 py-2 text-zinc-200">{program.ph}</td>
                                        <td className="px-3 py-2 text-zinc-200">{program.lights_on}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                ) : null}
            </Card>

            <Card className="overflow-visible border-zinc-700 bg-zinc-950/75 shadow-lg ring-1 ring-zinc-800/60">
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                    <div className="min-w-0">
                        <CardTitle className="text-base text-zinc-50">Sensor & controller groups</CardTitle>
                        <CardDescription className="text-zinc-400">
                            Map hardware for each control loop detected on this system.
                        </CardDescription>
                    </div>
                    {sectionHeaderBtn(groupsOpen, setGroupsOpen)}
                </CardHeader>
                {groupsOpen ? (
                    <CardContent className="space-y-4 overflow-visible pt-0">
                        {completedSystems.length === 0 ? (
                            <p className="text-sm text-zinc-500">
                                No complete automatic loops yet (add matching sensors and pumps/outlets per recipe).
                            </p>
                        ) : (
                            completedSystems.map((sys: any) => (
                                <div
                                    key={String(sys.variable)}
                                    className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4"
                                >
                                    <h4 className="mb-3 text-sm font-semibold capitalize text-emerald-200/90">
                                        {sys.variable}
                                    </h4>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={fieldLabel}>Controllers</span>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 border-zinc-600 bg-zinc-950 text-xs hover:bg-zinc-800"
                                                    onClick={() => void handleEdit(sys.variable, 'edit_controllers')}
                                                >
                                                    Edit
                                                </Button>
                                            </div>
                                            <div className="min-h-[1.5rem] text-sm text-zinc-300">
                                                {chooseGroupType(sys.variable, 'controllers', sys.controllers_info)}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={fieldLabel}>Sensors</span>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 border-zinc-600 bg-zinc-950 text-xs hover:bg-zinc-800"
                                                    onClick={() => void handleEdit(sys.variable, 'edit_sensors')}
                                                >
                                                    Edit
                                                </Button>
                                            </div>
                                            <div className="min-h-[1.5rem] text-sm text-zinc-300">
                                                {chooseGroupType(sys.variable, 'sensors', sys.sensors)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </CardContent>
                ) : null}
            </Card>

            <Card className="overflow-visible border-zinc-700 bg-zinc-950/75 shadow-lg ring-1 ring-zinc-800/60">
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                    <div className="min-w-0">
                        <CardTitle className="text-base text-zinc-50">Advanced options</CardTitle>
                        <CardDescription className="text-zinc-400">
                            Lights, dosing, tolerances, and pump schedule. Turn off defaults to customize.
                        </CardDescription>
                    </div>
                    {sectionHeaderBtn(advancedOpen, setAdvancedOpen)}
                </CardHeader>
                {advancedOpen ? (
                    <CardContent className="space-y-6 overflow-visible pt-0">
                        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-3">
                            <input
                                id="auto-form-use-defaults"
                                type="checkbox"
                                className="size-4 rounded border-zinc-600 bg-zinc-950 text-emerald-600 focus:ring-emerald-500"
                                checked={defaultOptions}
                                onChange={(e) => {
                                    const checked = e.target.checked
                                    setDefaultOptions(checked)
                                    if (checked) {
                                        setAdvancedOptionsState({ ...DEFAULT_ADVANCED_OPTIONS })
                                    }
                                }}
                            />
                            <label htmlFor="auto-form-use-defaults" className="text-sm text-zinc-200">
                                Use default advanced settings
                            </label>
                        </div>

                        <fieldset disabled={defaultOptions} className="min-w-0 space-y-6 border-0 p-0">
                            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                                <h4 className="mb-3 text-sm font-semibold text-zinc-200">Light schedule</h4>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className={`mb-2 block ${fieldLabel}`}>Start</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileTimePicker
                                                onChange={(e) => {
                                                    if (!e) return
                                                    setAdvancedOptionsState((prev: any) => ({
                                                        ...prev,
                                                        lights: {
                                                            ...prev.lights,
                                                            light_start_time: convertDateFormat(String(e)),
                                                        },
                                                    }))
                                                }}
                                                defaultValue={dayjs(advancedOptionsState.lights?.light_start_time)}
                                                orientation="landscape"
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div>
                                        <label className={`mb-2 block ${fieldLabel}`}>End</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileTimePicker
                                                onChange={(e) => {
                                                    if (!e) return
                                                    setAdvancedOptionsState((prev: any) => ({
                                                        ...prev,
                                                        lights: {
                                                            ...prev.lights,
                                                            light_end_time: convertDateFormat(String(e)),
                                                        },
                                                    }))
                                                }}
                                                defaultValue={dayjs(advancedOptionsState.lights?.light_end_time)}
                                                orientation="landscape"
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                                <h4 className="mb-3 text-sm font-semibold text-zinc-200">Dosing</h4>
                                <DoserSettings
                                    advancedOptionsState={advancedOptionsState}
                                    setAdvancedOptionsState={setAdvancedOptionsState}
                                />
                            </div>

                            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                                <h4 className="mb-4 text-sm font-semibold text-zinc-200">Tolerances</h4>
                                <div className="grid gap-6 sm:grid-cols-2">
                                    <div>
                                        <span className={fieldLabel}>pH</span>
                                        <RangeSlider
                                            type="ph"
                                            targetValue={program.ph}
                                            defaultValue={advancedOptionsState.tolerances?.ph}
                                            advancedOptionsState={advancedOptionsState}
                                            setAdvancedOptionsState={setAdvancedOptionsState}
                                        />
                                    </div>
                                    <div>
                                        <span className={fieldLabel}>EC</span>
                                        <RangeSlider
                                            type="ec"
                                            targetValue={program.ec}
                                            defaultValue={advancedOptionsState.tolerances?.ec}
                                            advancedOptionsState={advancedOptionsState}
                                            setAdvancedOptionsState={setAdvancedOptionsState}
                                        />
                                    </div>
                                    <div>
                                        <span className={fieldLabel}>Temperature</span>
                                        <RangeSlider
                                            type="temperature"
                                            targetValue={program.temperature}
                                            defaultValue={advancedOptionsState.tolerances?.temperature}
                                            advancedOptionsState={advancedOptionsState}
                                            setAdvancedOptionsState={setAdvancedOptionsState}
                                        />
                                    </div>
                                    <div>
                                        <span className={fieldLabel}>Humidity</span>
                                        <RangeSlider
                                            type="humidity"
                                            targetValue={program.humidity}
                                            defaultValue={advancedOptionsState.tolerances?.humidity}
                                            advancedOptionsState={advancedOptionsState}
                                            setAdvancedOptionsState={setAdvancedOptionsState}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                                <h4 className="mb-4 text-sm font-semibold text-zinc-200">Pump circulation</h4>
                                <div className="mb-4 flex items-center gap-2">
                                    <input
                                        id="pump-all-day"
                                        type="checkbox"
                                        className="size-4 rounded border-zinc-600 bg-zinc-950 text-emerald-600"
                                        checked={Boolean(advancedOptionsState.pump_all_day)}
                                        onChange={(e) =>
                                            setAdvancedOptionsState((prev: any) => ({
                                                ...prev,
                                                pump_all_day: e.target.checked,
                                            }))
                                        }
                                    />
                                    <label htmlFor="pump-all-day" className="text-sm text-zinc-200">
                                        Run circulation all day
                                    </label>
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                    <div>
                                        <label className={`mb-2 block ${fieldLabel}`}>Start</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileTimePicker
                                                onChange={(e) => {
                                                    if (!e) return
                                                    setAdvancedOptionsState((prev: any) => ({
                                                        ...prev,
                                                        pump_start_time: convertDateFormat(String(e)),
                                                    }))
                                                }}
                                                defaultValue={dayjs(advancedOptionsState.pump_start_time)}
                                                orientation="landscape"
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div>
                                        <label className={`mb-2 block ${fieldLabel}`}>End</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileTimePicker
                                                onChange={(e) => {
                                                    if (!e) return
                                                    setAdvancedOptionsState((prev: any) => ({
                                                        ...prev,
                                                        pump_end_time: convertDateFormat(String(e)),
                                                    }))
                                                }}
                                                defaultValue={dayjs(advancedOptionsState.pump_end_time)}
                                                orientation="landscape"
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                <div className="mt-4 grid gap-4 md:grid-cols-2">
                                    <div>
                                        <p className={`mb-2 ${fieldLabel}`}>Duration</p>
                                        <div className="flex flex-col gap-3 sm:flex-row">
                                            <TimeSlider
                                                type="pump-duration"
                                                time="min"
                                                defaultValue={advancedOptionsState.pump_duration?.minute}
                                                advancedOptionsState={advancedOptionsState}
                                                setAdvancedOptionsState={setAdvancedOptionsState}
                                            />
                                            <TimeSlider
                                                type="pump-duration"
                                                time="sec"
                                                defaultValue={advancedOptionsState.pump_duration?.seconds}
                                                advancedOptionsState={advancedOptionsState}
                                                setAdvancedOptionsState={setAdvancedOptionsState}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <p className={`mb-2 ${fieldLabel}`}>Interval</p>
                                        <div className="flex flex-col gap-3 sm:flex-row">
                                            <TimeSlider
                                                type="pump-interval"
                                                time="hour"
                                                defaultValue={advancedOptionsState.pump_interval?.hour}
                                                advancedOptionsState={advancedOptionsState}
                                                setAdvancedOptionsState={setAdvancedOptionsState}
                                            />
                                            <TimeSlider
                                                type="pump-interval"
                                                time="min"
                                                defaultValue={advancedOptionsState.pump_interval?.minute}
                                                advancedOptionsState={advancedOptionsState}
                                                setAdvancedOptionsState={setAdvancedOptionsState}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </CardContent>
                ) : null}
            </Card>
        </div>
    )
}

export default AutomaticProgram
